import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChevronDown, ChevronUp, Brain, Link as ChainIcon } from 'lucide-react';

interface ThinkingBlockProps {
  children: React.ReactNode;
}

export const ThinkingBlock: React.FC<ThinkingBlockProps> = ({ children }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // Split content into thinking and response parts
  const content = typeof children === 'string' 
    ? children
    : '';

  // Use a safer approach to extract thinking content to avoid regex issues
  let thinkingContent = '';
  let responseContent = content;
  
  try {
    const thinkMatch = content.match(/<think>([\s\S]*?)<\/think>([\s\S]*)/);
    if (thinkMatch) {
      thinkingContent = thinkMatch[1].trim();
      responseContent = thinkMatch[2].trim();
    }
  } catch (error) {
    console.error('Error parsing thinking content:', error);
    // Fallback to simple string methods if regex fails
    const thinkStart = content.indexOf('<think>');
    const thinkEnd = content.indexOf('</think>');
    if (thinkStart !== -1 && thinkEnd !== -1 && thinkEnd > thinkStart) {
      thinkingContent = content.substring(thinkStart + 7, thinkEnd).trim();
      responseContent = content.substring(thinkEnd + 8).trim();
    }
  }

  return (
    <div className="space-y-4 ml-16"> {/* Added margin-left here */}
      {/* Thinking section */}
      <div className="my-4 relative">
        {/* Brain icon and collapse button */}
        <div className="absolute -left-12 top-0 flex flex-col items-center gap-2">
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900">
            <Brain className="w-5 h-5 text-orange-600 dark:text-orange-300" />
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 transition-colors"
            title={isExpanded ? "Collapse thinking" : "Expand thinking"}
          >
            {isExpanded ? (
              <ChevronUp className="w-4 h-4 text-gray-600 dark:text-gray-300" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-300" />
            )}
          </button>
        </div>

        {/* Thinking content */}
        <div className={`transition-all duration-300 ${isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
          <div className="p-4 bg-orange-50 dark:bg-orange-900/30 rounded-lg border-l-4 border-orange-500">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              className="text-sm text-orange-900 dark:text-orange-100 whitespace-pre-wrap max-h-[400px] overflow-y-auto thinking-content-scroll"
              components={{
                a: ({href, children}) => (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                  >
                    {children}
                    <ChainIcon className="w-4 h-4" />
                  </a>
                )
              }}
            >
              {thinkingContent}
            </ReactMarkdown>
          </div>
        </div>

        {/* Decorative elements */}
        <div className={`absolute left-4 top-8 bottom-0 w-0.5 bg-orange-200 dark:bg-orange-800 transition-opacity duration-300 ${isExpanded ? 'opacity-100' : 'opacity-0'}`}></div>
        <div className={`absolute -left-3 top-8 w-3 h-3 bg-orange-400 dark:bg-orange-600 rounded-full transition-opacity duration-300 ${isExpanded ? 'opacity-100' : 'opacity-0'}`}></div>
      </div>

      {/* Response section */}
      {responseContent && (
        <div className={`mt-4 transition-all duration-300 ${!isExpanded ? 'opacity-100' : 'opacity-70'}`}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            className="prose prose-sm max-w-none dark:prose-invert"
            components={{
              a: ({href, children}) => (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                >
                  {children}
                  <ChainIcon className="w-4 h-4" />
                </a>
              )
            }}
          >
            {responseContent}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
};

// Helper functions remain the same
export const wrapInThinkTags = (text: string) => {
  if (text.includes('<think>')) return text;
  return `<think>${text}</think>`;
};

export const extractThinkingContent = (text: string) => {
  try {
    // First try with regex, but use a safer pattern
    const match = text.match(/<think>([\s\S]*?)<\/think>/);
    if (match) {
      return match[1].trim();
    }
  } catch (error) {
    console.error('Error extracting thinking content with regex:', error);
  }
  
  // Fallback to string methods if regex fails
  const thinkStart = text.indexOf('<think>');
  const thinkEnd = text.indexOf('</think>');
  if (thinkStart !== -1 && thinkEnd !== -1 && thinkEnd > thinkStart) {
    return text.substring(thinkStart + 7, thinkEnd).trim();
  }
  
  return text;
};